import React, { useEffect, useMemo, useState } from "react";
import ComponentCustomModal from "../../components/common/customModal/ComponentCustomModal";
import { FtextField } from "../../components/common/FtextField";
import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import Loader from "../../components/common/Loader";
import { useDispatch, useSelector } from "react-redux";
import useISPowner from "../../hooks/useISPOwner";
import { createNetworkPoint } from "../../features/oltApi";
import { getArea, getNetworkDevice } from "../../features/apiCalls";
import useSelectorState from "../../hooks/useSelectorState";
import {
  getPoleBoxApi,
  getSubAreasApi,
} from "../../features/actions/customerApiCall";

import * as Yup from "yup";
const NetworkDialog = ({ show, setShow, isUpdate, networkPoint }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const { ispOwnerId } = useISPowner();

  //---> Get redux store state data from useSelectorState hooks
  const { areas, subAreas, polesBox } = useSelectorState();

  const devices = useSelector((state) => state.network?.devices);

  // =================> device options
  const areaOptions = useMemo(() => {
    return (
      areas?.map((item) => ({
        name: item?.name,
        value: item?.id,
      })) || []
    );
  }, [areas]);

  const subAreaOptions = useMemo(() => {
    return (
      subAreas?.map((item) => ({
        name: item?.name,
        value: item?.id,
      })) || []
    );
  }, [subAreas]);

  const polesBoxOptions = useMemo(() => {
    return (
      polesBox?.map((item) => ({
        name: item?.name,
        value: item?.id,
      })) || []
    );
  }, [polesBox]);

  // =================> device options
  const deviceOptions = useMemo(() => {
    return (
      devices?.map((item) => ({
        name: item?.name,
        value: item?.id,
      })) || []
    );
  }, [devices]);

  // =================> form fields
  const inputOption = [
    {
      type: "text",
      id: "name",
      name: "name",
      label: t("name"),
      isVisible: true,
    },
    {
      as: "select",
      id: "area",
      name: "area",
      label: t("area"),
      options: areaOptions,
      textAccessor: "name",
      valueAccessor: "value",
      isVisible: true,
      firstOptions: t("selectArea"),
    },
    {
      as: "select",
      id: "subArea",
      name: "subArea",
      label: t("subArea"),
      options: subAreaOptions,
      textAccessor: "name",
      valueAccessor: "value",
      isVisible: true,
      firstOptions: t("selectSubArea"),
    },
    {
      as: "select",
      id: "poleBox",
      name: "poleBox",
      label: t("poleBox"),
      options: polesBoxOptions,
      textAccessor: "name",
      valueAccessor: "value",
      isVisible: true,
      firstOptions: t("selectPoleBox"),
    },
    {
      as: "select",
      id: "device",
      name: "device",
      label: t("device"),
      options: deviceOptions,
      textAccessor: "name",
      valueAccessor: "value",
      isVisible: true,
      firstOptions: t("selectDevice"),
    },
    {
      as: "textarea",
      type: "textarea",
      id: "description",
      name: "description",
      label: t("description"),
      isVisible: true,
    },
  ];

  // =================> initial values (match with fields)
  const initialValues = {
    name: networkPoint?.name || "",
    area: networkPoint?.area || "",
    subArea: networkPoint?.subArea || "",
    poleBox: networkPoint?.poleBox || "",
    device: networkPoint?.device || "",
    description: networkPoint?.description || "",
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t("nameRequired")).min(3, t("nameMinLength")),

    area: Yup.string().required(t("areaRequired")),

    subArea: Yup.string().required(t("subAreaRequired")),

    poleBox: Yup.string(),

    device: Yup.string().required(t("deviceRequired")),

    description: Yup.string().max(200, t("descriptionMaxLength")),
  });

  // =================> submit
  const handleSubmit = (values, { resetForm }) => {
    console.log(values);

    const payload = {
      ...values,
      ispOwnerId,
    };

    createNetworkPoint(dispatch, payload, setIsLoading, setShow, resetForm);
  };

  useEffect(() => {
    getNetworkDevice(dispatch, ispOwnerId, setIsLoading);
  }, []);

  useEffect(() => {
    //---> @Get ispOwner areas data
    !areas?.length && getArea(dispatch, ispOwnerId, setIsLoading);

    //---> @Get ispOwner areas sub-area data
    !subAreas?.length && getSubAreasApi(dispatch, ispOwnerId);

    //---> @Get ispOwner sub-areas pol-box data
    !polesBox?.length && getPoleBoxApi(dispatch, ispOwnerId, setIsLoading);
  }, []);

  return (
    <ComponentCustomModal show={show} setShow={setShow} centered size="md">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize={true} // This ensures the form is updated when device prop changes
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <div className="mb-3">
              {inputOption.map(
                (item, index) =>
                  item.isVisible && (
                    <FtextField key={item.id || index} {...item} />
                  ),
              )}
            </div>

            {isLoading && <Loader />}

            <div className="d-flex justify-content-end">
              <button
                type="submit"
                className="btn btn-success"
                disabled={isSubmitting || isLoading}
              >
                {t("submit")}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </ComponentCustomModal>
  );
};

export default NetworkDialog;
